import { useCallback, useEffect, useRef } from "react"
import { trackFathomEvent } from "utils"

/**
 * Debounced Fathom event tracker for React components.
 * Delays event firing until `delay` ms have passed with no new calls (default 2500ms,
 * tolerant of users who pause to review results as they type).
 * Resets the timer whenever `deps` change.
 * Does not trigger tracking event if any metadata value is empty after trimming.
 * Does not fire on initial render — deps with a value of 0 or "" are treated as uninitialized.
 *
 * Returns a `flush()` function that fires the pending event immediately and cancels the
 * timer. Use it to capture a fast commit (e.g. the user types then hits Enter / clicks a
 * result before the debounce elapses). It is a no-op when nothing is pending, so it can
 * never double-count an event the debounce already fired.
 *
 * ⚠️ Component Lifecycle: The component must remain mounted until the event fires.
 * - For stateful changes (search, filters): component stays mounted naturally
 * - For links/navigation: use `trackFathomEvent` directly instead
 * @param eventName - The event name (e.g., "map search")
 * @param metadata - Event metadata object
 * @param deps - React dependency array
 * @param options - Optional configuration (delay defaults to 2500ms, debug flag)
 * @returns flush - fires the pending event now (no-op if nothing is pending)
 */
export function useDebouncedFathomTracking(
  eventName: string,
  metadata: Record<string, string | number>,
  deps: unknown[],
  options: { delay?: number; debug?: boolean } = {}
): () => void {
  const { delay = 2500, debug } = options

  // Single source of truth for "an event is pending": a live timer id, else null.
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  // Latest values so flush() fires with the current event name/metadata.
  const latestRef = useRef({ eventName, metadata, debug })
  latestRef.current = { eventName, metadata, debug }

  const fire = useCallback(() => {
    const { eventName, metadata, debug } = latestRef.current

    // Skip if any metadata value is empty after trimming
    const hasEmptyValue = Object.entries(metadata).some(([, value]) => {
      return String(value).trim() === ""
    })
    if (hasEmptyValue) {
      // eslint-disable-next-line no-console
      if (debug) console.log(`[fathom] skipped (empty metadata value)`)
      return
    }

    trackFathomEvent(eventName, metadata, debug !== undefined ? { debug } : {})
  }, [])

  const flush = useCallback(() => {
    if (timerRef.current === null) return // nothing pending — never double-fire
    clearTimeout(timerRef.current)
    timerRef.current = null
    fire()
  }, [fire])

  useEffect(() => {
    // Skip if any dep is falsy (0 or "") — indicates uninitialized/initial render state
    if (deps.some((d) => !d)) return

    timerRef.current = setTimeout(() => {
      timerRef.current = null
      fire()
    }, delay)
    return () => {
      if (timerRef.current !== null) {
        clearTimeout(timerRef.current)
        timerRef.current = null
      }
    }
  }, deps)

  return flush
}
